import { useEffect, useState } from "react";
import { ShieldCheck, BadgeCheck, Sparkles, Wallet } from "lucide-react";

/**
 * EstimatedPricingPanel — renders directly above the final submit button on /book.
 * - Pulls the live quote from POST /api/quotes using current form state
 * - Displays: vehicle-specific total + ±8% range band + 4 reassurance chips
 * - Marketing prices live on /pricing; this panel is anchored to the real quote engine.
 */
const API = process.env.REACT_APP_BACKEND_URL;

const CHIPS = [
  { icon: BadgeCheck,  label: "Transparent Flat-Rate Pricing" },
  { icon: ShieldCheck, label: "No Hidden Fees" },
  { icon: Sparkles,    label: "Luxury Chauffeur Included" },
  { icon: Wallet,      label: "Confirmed instantly before payment" },
];

function money(n) {
  return `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const CATEGORY_LABEL = {
  Luxury_Sedan: "Business Sedan",
  Premium_SUV: "Luxury SUV",
  Executive_Sprinter: "Executive Sprinter Van",
  Motor_Coach: "Motor Coach",
};

export default function EstimatedPricingPanel({
  serviceType,
  pickupAddress,
  dropoffAddress,
  vehicleCategory,
  pickupDatetime,
  durationHours,
}) {
  const [state, setState] = useState({ status: "idle", quote: null, error: null });

  useEffect(() => {
    const ready =
      pickupAddress &&
      vehicleCategory &&
      pickupDatetime &&
      (serviceType === "hourly" ? durationHours > 0 : Boolean(dropoffAddress));
    if (!ready) {
      setState({ status: "idle", quote: null, error: null });
      return;
    }

    let cancelled = false;
    setState((s) => ({ ...s, status: "loading", error: null }));

    const body = {
      service_type: serviceType,
      pickup_address: pickupAddress,
      dropoff_address: dropoffAddress || undefined,
      vehicle_category: vehicleCategory,
      pickup_datetime: pickupDatetime,
      duration_hours: serviceType === "hourly" ? Number(durationHours) : undefined,
    };

    fetch(`${API}/api/quotes`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    })
      .then(async (r) => {
        const j = await r.json();
        if (cancelled) return;
        if (!r.ok) {
          setState({ status: "error", quote: null, error: j?.detail || "Unable to price" });
          return;
        }
        setState({ status: "ready", quote: j.quote, error: null });
      })
      .catch((e) => {
        if (!cancelled) setState({ status: "error", quote: null, error: String(e) });
      });

    return () => {
      cancelled = true;
    };
  }, [serviceType, pickupAddress, dropoffAddress, vehicleCategory, pickupDatetime, durationHours]);

  const totalCents = state.quote?.total_price
    ? Math.round(Number(state.quote.total_price) * 100)
    : null;
  const lowCents = totalCents ? Math.round(totalCents * 0.92) : null;
  const highCents = totalCents ? Math.round(totalCents * 1.08) : null;

  const displayName = CATEGORY_LABEL[vehicleCategory] || vehicleCategory || "Selected Vehicle";

  return (
    <section
      data-testid="estimated-pricing-panel"
      className="relative backdrop-blur-xl bg-gradient-to-br from-[#121214]/85 to-[#0B0B0C]/85 border border-[#C5A24A]/50 px-6 md:px-8 py-7 md:py-8"
    >
      <div className="flex items-center justify-between gap-4 mb-5">
        <div>
          <div className="text-[10px] uppercase tracking-[0.35em] text-[#C5A24A] mb-2">
            Live Estimate
          </div>
          <h3 className="font-serif text-xl md:text-2xl text-white tracking-tight">
            Your Estimated Quote
          </h3>
        </div>
        {state.status === "ready" && (
          <div
            data-testid="estimated-pricing-range"
            className="hidden sm:flex flex-col items-end"
          >
            <span className="text-[10px] uppercase tracking-[0.28em] text-[#8E9AA6]">Range</span>
            <span className="font-serif text-lg text-white tabular-nums">
              {money(lowCents / 100)}
              <span className="text-[#8E9AA6] mx-2">–</span>
              {money(highCents / 100)}
            </span>
          </div>
        )}
      </div>

      {state.status === "idle" && (
        <p
          data-testid="estimated-pricing-idle"
          className="text-sm text-[#8E9AA6] font-light leading-relaxed"
        >
          Fill in pickup, drop-off (or duration for hourly), and vehicle class — your live
          estimate will appear here.
        </p>
      )}

      {state.status === "loading" && (
        <p
          data-testid="estimated-pricing-loading"
          className="text-sm text-[#E6E6E6]/80 font-light animate-pulse"
        >
          Calculating your estimated fare…
        </p>
      )}

      {state.status === "error" && (
        <p
          data-testid="estimated-pricing-error"
          className="text-sm text-red-300/90 font-light"
        >
          Unable to compute an estimate for the current inputs. Refine the addresses or call
          our dispatch on{" "}
          <a href="tel:+13475230763" className="text-white underline">
            +1-347-523-0763
          </a>
          .
        </p>
      )}

      {state.status === "ready" && state.quote && (
        <div data-testid="estimated-pricing-ready">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5">
            <div>
              <div className="text-[11px] uppercase tracking-[0.32em] text-[#8E9AA6] mb-2">
                {displayName}
              </div>
              <div
                data-testid="estimated-pricing-total"
                className="font-serif text-4xl md:text-5xl text-white tracking-tighter tabular-nums leading-none"
              >
                {money(state.quote.total_price)}
              </div>
              {state.quote.distance_miles ? (
                <div className="text-xs text-[#8E9AA6] mt-2 tabular-nums">
                  {Number(state.quote.distance_miles).toFixed(1)} miles · gratuity {money(state.quote.gratuity)} · airport{" "}
                  {money(state.quote.airport_surcharge)}
                </div>
              ) : null}
            </div>
            <div className="sm:hidden">
              <span className="text-[10px] uppercase tracking-[0.28em] text-[#8E9AA6]">Range</span>
              <div className="font-serif text-base text-white tabular-nums">
                {money(lowCents / 100)}
                <span className="text-[#8E9AA6] mx-2">–</span>
                {money(highCents / 100)}
              </div>
            </div>
          </div>

          <p className="text-xs md:text-sm text-[#8E9AA6] font-light mt-6 leading-relaxed">
            Your final quote will be confirmed instantly before payment.
          </p>
        </div>
      )}

      <div className="mt-7 grid grid-cols-2 md:grid-cols-4 gap-2.5">
        {CHIPS.map((c) => {
          const Icon = c.icon;
          return (
            <div
              key={c.label}
              className="flex items-center gap-2 border border-white/10 px-3 py-2.5 text-[10.5px] uppercase tracking-[0.16em] text-[#E6E6E6]/85 bg-white/[0.02]"
            >
              <Icon size={13} strokeWidth={1.5} className="text-[#C5A24A] shrink-0" />
              <span className="leading-tight">{c.label}</span>
            </div>
          );
        })}
      </div>
    </section>
  );
}
